import { format, isValid, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { CalendarDays } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

interface DateRangeFilterInputProps {
  label?: string;
  value?: DateRange;
  onChange?: (range: DateRange | undefined) => void;
  className?: string;
}

function applyDateMask(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

function formatDateValue(date: Date | undefined): string {
  return date && isValid(date) ? format(date, 'dd/MM/yyyy') : '';
}

function parseDateInput(value: string): Date | undefined {
  if (value.length !== 10) return undefined;
  const parsed = parse(value, 'dd/MM/yyyy', new Date());
  return isValid(parsed) ? parsed : undefined;
}

export function DateRangeFilterInput({
  label,
  value,
  onChange,
  className,
}: DateRangeFilterInputProps) {
  const [open, setOpen] = React.useState(false);
  const [fromInput, setFromInput] = React.useState(() => formatDateValue(value?.from));
  const [toInput, setToInput] = React.useState(() => formatDateValue(value?.to));

  React.useEffect(() => {
    setFromInput(formatDateValue(value?.from));
    setToInput(formatDateValue(value?.to));
  }, [value]);

  const emitChange = (from: Date | undefined, to: Date | undefined) => {
    if (!from && !to) {
      onChange?.(undefined);
    } else {
      onChange?.({ from, to });
    }
  };

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setFromInput(masked);
    const parsed = parseDateInput(masked);
    if (parsed || masked.length === 0) {
      emitChange(parsed, value?.to);
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setToInput(masked);
    const parsed = parseDateInput(masked);
    if (parsed || masked.length === 0) {
      emitChange(value?.from, parsed);
    }
  };

  const handleCalendarSelect = (range: DateRange | undefined) => {
    onChange?.(range);
    if (range?.from && range?.to) {
      setOpen(false);
    }
  };

  return (
    <div className={cn('w-full', className)}>
      {label && <span className="block text-sm font-medium text-gray-700 mb-2">{label}</span>}
      <Popover open={open} onOpenChange={setOpen}>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <input
              type="text"
              inputMode="numeric"
              placeholder="De dd/mm/aaaa"
              value={fromInput}
              onChange={handleFromChange}
              className="flex h-9 w-full rounded-md border border-gray-300 bg-transparent px-3 py-1 pr-9 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <CalendarDays size={16} />
              </button>
            </PopoverTrigger>
          </div>
          <div className="relative flex-1">
            <input
              type="text"
              inputMode="numeric"
              placeholder="Até dd/mm/aaaa"
              value={toInput}
              onChange={handleToChange}
              className="flex h-9 w-full rounded-md border border-gray-300 bg-transparent px-3 py-1 pr-9 text-sm shadow-sm transition-colors placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500"
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <CalendarDays size={16} />
              </button>
            </PopoverTrigger>
          </div>
        </div>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="range"
            selected={value}
            onSelect={handleCalendarSelect}
            locale={ptBR}
            defaultMonth={value?.from}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
